import { animate, state, style, transition, trigger } from '@angular/animations';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Component, computed, effect, OnDestroy, OnInit, signal, inject } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountGroup, AccountGroupExternalIdentifiers, AccountGroupLocation } from '@galaxy/account-group';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { debounceTime, map, skipWhile, takeUntil } from 'rxjs/operators';
import { PartnerService } from '../core/partner.service';
import { User } from '../core/user';
import { AccountGroupPermissionsService } from '../user-permissions/account-group-permissions/account-group-permissions.service';
import { indicationRotate } from '../user-permissions/expansion-panel.animations';
import { UserService } from '../users';

@Component({
  selector: 'app-user-notifications',
  templateUrl: './user-notifications.component.html',
  styleUrls: ['./user-notifications.component.scss', '../user-permissions/table.scss'],
  animations: [
    indicationRotate,
    trigger('detailExpand', [
      state('void', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('*', style({ height: '*', visibility: 'visible' })),
      transition('void <=> *', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false,
})
export class UserNotificationsComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);

  allNotificationsEnabled = signal(false);
  partnerNotificationEnabled = toSignal(this.partnerService.partnerNotificationEnabled$);

  partnerId = this.partnerService.partnerId;
  userId = toSignal(this.route.params.pipe(map((params) => params.userId)));
  userResource = rxResource({
    request: () => {
      const userId = this.userId();
      if (!userId) return null;
      return { userId, fieldMask: ['SUBJECT_ID', 'USER_ID', 'EMAIL', 'ATTRIBUTES'] };
    },
    loader: ({ request }) => {
      if (!request) return of(null);
      return this.userService.getUser$(request.userId, request.fieldMask);
    },
  });
  unifiedUserId = computed(() => this.userResource.value()?.unifiedUserId);

  readonly positions: ConnectedPosition[] = [{ ...PopoverPositions.Right }, { ...PopoverPositions.Bottom }];

  displayedColumns = ['name', 'indicator'];
  expandedPanels: string[] = [];

  loadingAccountGroups$: Observable<boolean> = this.accountGroupPermissionsService.loadingAccountGroups$;
  associatedAccountGroupsPage$: Observable<AccountGroup[]> =
    this.accountGroupPermissionsService.associatedAccountGroupsPage$.pipe(
      map((accountGroups) => {
        return [
          new AccountGroup({
            accountGroupId: '',
            napData: new AccountGroupLocation({ companyName: 'Default (All accounts)' }),
            externalIdentifiers: new AccountGroupExternalIdentifiers({ marketId: '' }),
          }),
          ...accountGroups,
        ];
      }),
    );
  total$: Observable<number> = this.accountGroupPermissionsService.total$;
  pageSize$: Observable<number> = this.accountGroupPermissionsService.pageSize$;
  pageIndex$: Observable<number> = this.accountGroupPermissionsService.pageIndex$;
  private search$$: BehaviorSubject<string> = new BehaviorSubject('');
  private componentDestroyed$$: Subject<any> = new Subject();
  constructor(
    private userService: UserService,
    private route: ActivatedRoute,
    private snackbarService: SnackbarService,
    private accountGroupPermissionsService: AccountGroupPermissionsService,
    private partnerService: PartnerService,
  ) {
    effect(() => {
      this.accountGroupPermissionsService.loadUser(this.userId());
    });

    effect(() => {
      this.allNotificationsEnabled.set(!!this.userResource.value()?.notificationsEnabled);
    });
  }

  ngOnInit(): void {
    this.accountGroupPermissionsService.init();
    this.search$$
      .pipe(
        takeUntil(this.componentDestroyed$$),
        skipWhile((s) => !s),
        debounceTime(500),
      )
      .subscribe((searchTerm) => this.accountGroupPermissionsService.updateSearch(searchTerm));
  }

  ngOnDestroy(): void {
    this.componentDestroyed$$.next('bye');
  }

  panelOpen(accountGroupId: string): void {
    if (this.expandedPanels.indexOf(accountGroupId) === -1) {
      this.expandedPanels.push(accountGroupId);
    }
  }

  panelClose(accountGroupId: string): void {
    const index = this.expandedPanels.indexOf(accountGroupId);
    if (index >= 0) {
      this.expandedPanels.splice(index, 1);
    }
  }

  getExpandedState(accountGroupId: string): string {
    return this.expandedPanels.indexOf(accountGroupId) >= 0 ? 'expanded' : 'collapsed';
  }

  paginationChange(event: PageEvent): void {
    this.accountGroupPermissionsService.updatePagination(event);
  }

  address(accountGroup: AccountGroup): string {
    return [
      accountGroup.napData.address,
      accountGroup.napData.city,
      accountGroup.napData.state,
      accountGroup.napData.country,
    ]
      .filter((p) => !!p)
      .join(', ');
  }

  toggleGlobalNotifications(user: User, checked: boolean): void {
    this.userService.toggleNotificationsEnabled(user, checked).subscribe(
      (_) => {
        user.notificationsEnabled = checked;
        this.allNotificationsEnabled.set(checked);
        this.snackbarService.successSnack('Notifications updated successfully.');
      },
      (_) => {
        this.snackbarService.errorSnack('Notifications update failed. Try again.');
      },
    );
  }

  search(searchTerm: string): void {
    this.search$$.next(searchTerm);
  }

  trackById(index: number, item: AccountGroup): string {
    return item.accountGroupId;
  }

  onConfigurePartnerNotificationActionClick(): void {
    this.router.navigateByUrl('/customize-business-app/notifications');
  }
}
